import loadable from '@loadable/component';
import { defineMessages } from 'react-intl';
import { composeSchema } from '@plone/volto/helpers';

import Matomo from 'rer-theme/components/Matomo/Matomo';
import { changeSimpleCardTemplateOptions } from 'rer-theme/config/Blocks/ListingOptions/simpleCardTemplate';
import { changeCardWithImageTemplateOptions } from 'rer-theme/config/Blocks/ListingOptions/cardWithImageTemplate';
import { changeInEvidenceTemplateOptions } from 'rer-theme/config/Blocks/ListingOptions/inEvidenceTemplate';
import { changeMapTemplateOptions } from 'rer-theme/config/Blocks/ListingOptions/mapTemplate';
import { appyBlockExtraTags } from 'rer-theme/config/Blocks/ExtraTags';
import FogliaFilterBlocks from 'rer-theme/config/FogliaFilterBlocks';

import searchIcon from 'bootstrap-italia/src/svg/it-search.svg';
import {
  SearchPubblicazioniEdit,
  SearchPubblicazioniView,
} from 'rer-theme/components/Blocks/SearchPubblicazioni';

import IframeIcon from '@plone/volto/icons/application.svg';
import { IframeView, IframeEdit } from 'rer-theme/components/Blocks/Iframe';

import getRerViews from 'rer-theme/config/Views';
import { InvalidatePageAction } from 'rer-theme/components';
import ExportContentsView from 'rer-theme/components/View/ExportContentsView/ExportContentsView';

import CharCounterTextWidget from 'design-comuni-plone-theme/components/ItaliaTheme/manage/Widgets/CharCounterTextWidget';
import TextWidget from '@plone/volto/components/manage/Widgets/TextWidget';

import counterSVG from 'rer-theme/icons/counter.svg';
import {
  RemoteCounterEdit,
  RemoteCounterView,
} from 'rer-theme/components/Blocks/RemoteCounter';

import { addContentApiExpander } from 'rer-theme/helpers/settings';
import { getImageBlockSizes } from 'rer-theme/helpers/utils';
import {
  invalidatePageReducer,
  exportContentsReducer,
} from 'rer-theme/reducers';

const messages = defineMessages({
  textarea_max_length: {
    id: 'textarea_max_length',
    defaultMessage: 'Lunghezza massima del testo inseribile',
  },
  export_contents_brdc: {
    id: 'export_contents_brdc_label',
    defaultMessage: 'Esporta contenuti',
  },
});
const ClimateClock = loadable(() =>
  import(
    /* webpackChunkName: "ClimateClock" */ 'rer-theme/components/ClimateClock/ClimateClock'
  ),
);

import { addListingVariation_List } from 'rer-theme/config/Blocks/ListingVariations/list';
import { addListingVariation_Timeline } from 'rer-theme/config/Blocks/ListingVariations/timeline';

import applyRERSlateConfig from 'rer-theme/config/Slate/config';

import { loadables as subsitesLoadables } from './subsites';
import { isValidREREmail } from 'rer-theme/helpers/email';
import getRepeatableContentBlockAsyncData from 'rer-theme/components/Blocks/RepeatableContent/getAsyncData';

const Chatbot = loadable(() =>
  import(
    /* webpackChunkName: "Chatbot" */ 'rer-theme/components/Chatbot/Chatbot'
  ),
);

const applyConfig = (config) => {
  const getLanguagesFromEnv = (value) => {
    return value?.split(/\s*,\s*/).filter(Boolean);
  };

  const isMultilingualEnv =
    (__CLIENT__ && window.env.RAZZLE_MULTILINGUAL_SITE) ||
    process.env.RAZZLE_MULTILINGUAL_SITE ||
    false;
  const isMultilingual =
    isMultilingualEnv == true || isMultilingualEnv === 'true';
  const supportedLanguagesEnv =
    (__CLIENT__ &&
      getLanguagesFromEnv(window.env.RAZZLE_MULTILINGUAL_LANGUAGES)) ||
    getLanguagesFromEnv(process.env.RAZZLE_MULTILINGUAL_LANGUAGES) ||
    [];
  const supportedLanguages =
    supportedLanguagesEnv?.length == 0 ? ['it'] : supportedLanguagesEnv;

  config.settings = {
    ...config.settings,

    // matomoSiteId: 'xxxxxxxxx',
    // matomoUrlBase: 'https://ingestion.webanalytics.italia.it/',
    // italiaThemeViewsConfig: {
    //   ...config.settings.italiaThemeViewsConfig,
    //   imagePosition: 'afterHeader', // possible values: afterHeader, documentBody
    // },
    siteProperties: {
      /*
        Per tutte le props di siteProperties, può essere definito un oggetto per i siti multilingua, strutturato in questo modo:
        {'default': _valore_della_prop, 'it': _valore_della_prop,...}
        Se l'oggetto non è strutturato in questo modo, viene usato l'oggetto stesso, altrimenti viene preso il valore della corrispondente alla prop della lingua corrente o alla prop 'default' se esiste.
      */
      ...config.settings.siteProperties,
      siteTitle: 'Sito di demo Regione Emilia-Romagna',
      siteSubtitle: '',
      parentSiteTitle: 'Regione Emilia-Romagna',
      parentSiteURL: 'https://www.regione.emilia-romagna.it/',
      subsiteParentSiteTitle: 'Sito di demo Regione Emilia-Romagna',
    },
    loadables: {
      ...config.settings.loadables,
      ...subsitesLoadables,
    },
    searchBlockTemplates: [...config.settings.searchBlockTemplates, 'list'],
    'volto-blocks-widget': {
      ...config.settings['volto-blocks-widget'],
      allowedBlocks: [
        ...config.settings['volto-blocks-widget'].allowedBlocks,
        'iframe',
        'gridBlock',
      ],
    },

    siteSearch: {
      extraParams: ['authors'],
    },
    appExtras: [
      ...config.settings.appExtras,
      { match: '', component: Matomo },
      {
        match: { path: '/', exact: true }, //il climate clock si può mettere solo in home page. Si deve fare un contenuto di tipo RawHtml il cui contenuto è <climate-clock id="climateclock"></climate-clock>
        component: ClimateClock,
        props: { onlyJS: true },
      },
      {
        match: { path: '/urp' }, //il chatbot dell si attiva solo sotto /urp e solo sul sito del portale (variabile d'ambiente testata nel componente)
        component: Chatbot,
      },
      {
        match: '',
        component: InvalidatePageAction,
      },
      // filtro per i blocchi del CT Foglia di informafamiglie
      {
        match: ['/add', '/**/add'],
        component: FogliaFilterBlocks,
        props: {},
      },
      {
        match: ['/edit', '/**/edit'],
        component: FogliaFilterBlocks,
        props: {},
      },
    ],
    isMultilingual,
    supportedLanguages,
  };
  const baseFormSchema = config.blocks.blocksConfig.form.formSchema;
  config.blocks = {
    ...config.blocks,
    blocksConfig: {
      ...config.blocks.blocksConfig,

      argumentsInEvidence: {
        ...config.blocks.blocksConfig.argumentsInEvidence,
        restricted: true,
      },

      iframe: {
        id: 'iframe',
        title: 'Iframe',
        icon: IframeIcon,
        group: 'common',
        view: IframeView,
        edit: IframeEdit,
        restricted: false,
        mostUsed: false,
        security: {
          addPermission: [],
          view: [],
        },
        sidebarTab: 1,
      },

      searchPubblicazioni: {
        id: 'searchPubblicazioni',
        title: 'Ricerca Pubblicazioni',
        icon: searchIcon,
        group: 'search',
        view: SearchPubblicazioniView,
        edit: SearchPubblicazioniEdit,
        restricted: false,
        mostUsed: false,
        security: {
          addPermission: [],
          view: [],
        },
        sidebarTab: 1,
      },

      'remote-counter': {
        id: 'remote-counter',
        title: 'Contatore remoto',
        icon: counterSVG,
        group: 'common',
        view: RemoteCounterView,
        edit: RemoteCounterEdit,
        restricted: false,
        mostUsed: false,
        security: {
          addPermission: [],
          view: [],
        },
        sidebarTab: 1,
      },
      form: {
        ...config.blocks.blocksConfig.form,
        // formSchema: (data) => {
        //   let schema = baseFormSchema(data);
        //   schema.properties.email_otp_verification.default = true;
        //   return schema;
        // },
        formSchema: composeSchema(baseFormSchema, ({ schema }) => {
          schema.properties.email_otp_verification.default = true;
          return schema;
        }),
        schemaValidators: {
          ...config.blocks.blocksConfig.form.schemaValidators,
          default_from: (data) => {
            return isValidREREmail(data.default_from)
              ? null
              : "L'e-mail inserita nel campo 'Mittente di default' deve essere un indirizzo e-mail regionale. Es.: xxx@regione.emilia-romagna.it";
          },
        },
        fieldTypeSchemaExtenders: {
          ...(config.blocks.blocksConfig.form.fieldTypeSchemaExtenders ?? {}),
          textarea: (intl) => {
            let extender = {
              properties: {
                max_length: {
                  title: intl.formatMessage(messages.textarea_max_length),
                  send_to_backend: false,
                  type: 'number',
                },
                //default:
              },
              fields: ['max_length'],
              required: [],
            };
            return extender;
          },
        },
      },
      repeatableContentBlock: {
        ...config.blocks.blocksConfig.repeatableContentBlock,
        getAsyncData: getRepeatableContentBlockAsyncData,
      },

      image: {
        ...config.blocks.blocksConfig.image,
        getSizes: getImageBlockSizes,
      },
    },
  };

  //rimozione di blocchi inutilizzati
  delete config.blocks.blocksConfig.searchBandi;
  delete config.blocks.blocksConfig.searchEvents;
  delete config.blocks.blocksConfig.searchSections;
  delete config.blocks.blocksConfig.searchUO;
  delete config.blocks.blocksConfig.description;
  delete config.blocks.blocksConfig.html;

  //remove gridGalleryTemplate listing template
  config.blocks.blocksConfig.listing.variations =
    config.blocks.blocksConfig.listing.variations.filter(
      (v) => v.id != 'gridGalleryTemplate',
    );

  changeSimpleCardTemplateOptions(config.blocks);
  changeCardWithImageTemplateOptions(config.blocks);
  changeInEvidenceTemplateOptions(config.blocks);
  changeMapTemplateOptions(config.blocks);

  addListingVariation_List(config.blocks);
  addListingVariation_Timeline(config.blocks);
  appyBlockExtraTags(config);

  getRerViews(config);

  //per fare in modo che il campo titolo del ct Notizia abbia il widget textarea
  delete config.widgets.id.title; //impostati in io-comune
  delete config.widgets.id.description; //impostati in io-comune
  config.widgets.default = CharCounterTextWidget;
  config.widgets.id.externalUrl = TextWidget; //serve per togliere il limite di 255 caratteri dal campo externalUrl del ct ExternalNews
  ///--------
  applyRERSlateConfig(config); //Slate config

  //add api-expander: 'immersive-reader'
  addContentApiExpander(config, 'immersive-reader');

  // reducers
  config.addonReducers = {
    ...config.addonReducers,
    invalidatePage: invalidatePageReducer,
    exportContents: exportContentsReducer,
  };
  config.addonRoutes = [
    ...config.addonRoutes,
    {
      path: '/export-contents',
      component: ExportContentsView,
      exact: true,
      breadcrumbs_title: messages.export_contents_brdc,
    },
  ];

  config.settings.nonContentRoutes = [
    ...config.settings.nonContentRoutes,
    /\/export-contents$/,
  ];

  return config;
};

export default applyConfig;
